import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { ZoomControl } from "react-leaflet";
import { Browser } from "leaflet";
import { Div, Section } from "trunx";
import FindTable from "./FindTable";
import CameraInfoPanel from "./info/CameraInfo";
import FeatureInfo from "./info/FeatureInfo";
import LightningInfo from "./info/LightningInfo";
import SensorInfo from "./info/SensorInfo";
import { IPanel } from "./models/Menus";
import { useRootStore } from "./models/Root";
import { ISelectedSensor } from "./models/SelectedSensor";
import NavMenu from "./NavMenu";
import BookmarksPanel from "./panels/BookmarksPanel";
import LayersPanel from "./panels/LayersPanel";
import ForecastPanel from "./panels/ForecastPanel";
import MeasurePanel from "./panels/MeasurePanel";
import RadarPanel from "./panels/RadarPanel";
import SensorsPanel from "./panels/SensorsPanel";
import TabularDataPane from "./panes/TabularData";
import LeafletMobileCard from "./shared/LeafletMobileCard";
import { LeafletPanelBody } from "./shared/LeafletPanel";

const MobileSensorInfo = ({ sensor }: { sensor: ISelectedSensor }) => {
  switch (sensor.pane) {
    case "summary":
      return <SensorInfo selected={sensor} />;
    case "tabular":
      return <TabularDataPane sensor={sensor.sensor} />;
    default:
      return <> </>;
  }
};

const MobileCard = observer(() => {
  const [sensorView, setSensorView] = useState<"select" | "legend">("select");
  const getPanel = (panel: IPanel) => {
    switch (panel) {
      case "layers":
        return LayersPanel;
      case "bookmarks":
        return BookmarksPanel;
      case "measure":
        return MeasurePanel;
      case "sensors":
        return SensorsPanel;
      case "radar":
        return RadarPanel;
      case "forecast":
        return ForecastPanel;
      case "find":
        return FindTable;
    }
  };

  const {
    menus: { panel, nav, closeNav, closePanel },
    sensors: { selected: selectedSensor },
    layers: { selectedCamera, selectedFeature, lightning },
  } = useRootStore();

  const drawerPanel =
    (panel === "sensors" && sensorView === "select") ||
    panel === "layers" ||
    panel === "bookmarks" ||
    panel === "forecast"
      ? panel
      : undefined;

  useEffect(() => {
    if (panel !== "sensors") setSensorView("select");
  }, [panel]);

  if (nav || drawerPanel) {
    const DrawerPanel = drawerPanel ? getPanel(drawerPanel) : undefined;
    return (
      <LeafletMobileCard>
        <Div
          className="mobile-nav-overlay"
          data-cy="mobile-nav-overlay"
        >
          <button
            className="mobile-nav-scrim"
            aria-label={nav ? "Close menu" : "Close panel"}
            onClick={nav ? closeNav : closePanel}
          />
          <Div
            className={`mobile-nav-panel${nav ? " has-nav-menu" : ""}`}
            data-cy="mobile-nav-panel"
          >
            {nav ? (
              <LeafletPanelBody>
                <Section>
                  <NavMenu />
                </Section>
              </LeafletPanelBody>
            ) : (
              drawerPanel === "sensors" ? (
                <Div className="mobile-drawer-content">
                  <SensorsPanel
                    mobileView="select"
                    onMobileSelect={() => setSensorView("legend")}
                  />
                </Div>
              ) : DrawerPanel ? (
                <Div className="mobile-drawer-content">
                  <DrawerPanel />
                </Div>
              ) : null
            )}
          </Div>
        </Div>
      </LeafletMobileCard>
    );
  } else if (panel === "sensors" && sensorView === "legend") {
    return (
      <LeafletMobileCard>
        <SensorsPanel mobileView="legend" />
      </LeafletMobileCard>
    );
  } else if (selectedSensor) {
    return (
      <LeafletMobileCard>
        <MobileSensorInfo sensor={selectedSensor} />
      </LeafletMobileCard>
    );
  } else if (selectedFeature) {
    return (
      <LeafletMobileCard>
        <FeatureInfo feature={selectedFeature} />
      </LeafletMobileCard>
    );
  } else if (
    lightning &&
    (lightning.showLegend || lightning.selected !== undefined)
  ) {
    return (
      <LeafletMobileCard>
        <LightningInfo lightning={lightning} legend={false} />
      </LeafletMobileCard>
    );
  } else if (selectedCamera !== undefined) {
    return (
      <LeafletMobileCard>
        <CameraInfoPanel camera={selectedCamera} />
      </LeafletMobileCard>
    );
  } else if (panel && panel !== "find") {
    const Panel = getPanel(panel);
    return (
      <LeafletMobileCard>
        <Panel />
      </LeafletMobileCard>
    );
  } else if (panel === "find") {
    return (
      <LeafletMobileCard>
        <FindTable />
      </LeafletMobileCard>
    );
  } else {
    return <> </>;
  }
});

const Mobile = () => {
  return (
    <>
      {!Browser.touch && <ZoomControl position="topright" />}
      <MobileCard />
    </>
  );
};

export default Mobile;
